import React, { useEffect, useState } from "react";

/**
 * ProposalReview — the coordinator review gate, extracted as a standalone,
 * reusable component.
 *
 * Fetches a document's pending proposals, lets the coordinator uncheck anything
 * wrong, and approves the rest (which creates draft entities on the pages).
 * Used by the standalone Review page and available to QuickStart.
 *
 * Props:
 *   clientId   — client UUID
 *   documentId — the SourceDocument holding proposals (the ISP, for combined
 *                extraction)
 *   onApproved(created, result) — called with the {items, goals, ...} counts of NEW
 *                records and the full approve response ({updated, sections_written,
 *                conflicts_raised}) after a successful approve
 */
export default function ProposalReview({ clientId, documentId, onApproved }) {
  const [proposals, setProposals] = useState(null);
  const [error, setError]         = useState(null);
  const [selItems,    setItems]    = useState(new Set());
  const [selGoals,    setGoals]    = useState(new Set());
  const [selPeople,   setPeople]   = useState(new Set());
  const [selRoutines, setRoutines] = useState(new Set());
  const [selSections, setSections] = useState(new Set());
  const [approving, setApproving]  = useState(false);

  useEffect(() => {
    let cancelled = false;
    setProposals(null);
    setError(null);
    fetch(`/api/clients/${clientId}/documents/${documentId}/proposals`)
      .then(r => {
        if (!r.ok) throw new Error(`Failed to load proposals (${r.status})`);
        return r.json();
      })
      .then(data => {
        if (cancelled) return;
        const p = data.proposals || {};
        const empty = !(p.items||[]).length && !(p.goals||[]).length && !(p.people||[]).length
                      && !(p.routines||[]).length && !Object.keys(p.section_texts||{}).length;
        if (data.error || empty) {
          // Failed extraction: the document is "extracted" with an error and no
          // proposals. Show the reason, not an empty review (David, Oct 15).
          setError(`Extraction didn't produce anything to review. ${data.error || "The document may be empty or unreadable."}`);
          return;
        }
        setProposals(p);
        setItems(new Set((p.items || []).map((_, i) => i)));
        setGoals(new Set((p.goals || []).map((_, i) => i)));
        setPeople(new Set((p.people || []).map((_, i) => i)));
        setRoutines(new Set((p.routines || []).map((_, i) => i)));
        setSections(new Set(Object.keys(p.section_texts || {})));
      })
      .catch(e => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [clientId, documentId]);

  function toggle(setter) {
    return (idx, checked) => setter(prev => {
      const next = new Set(prev);
      if (checked) next.add(idx); else next.delete(idx);
      return next;
    });
  }

  async function approve() {
    setApproving(true);
    try {
      const res = await fetch(`/api/clients/${clientId}/documents/${documentId}/approve`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items:    [...selItems],
          goals:    [...selGoals],
          people:   [...selPeople],
          routines: [...selRoutines],
          section_keys: [...selSections],
        }),
      });
      let data = null;
      try { data = await res.json(); } catch { data = null; }
      if (!res.ok) throw new Error(`Approve failed (${res.status})${data?.error ? `: ${data.error}` : ""}. Nothing was saved.`);
      onApproved?.(data?.created || {}, data || {});
    } catch (e) {
      setError(e.message);
    } finally {
      setApproving(false);
    }
  }

  if (error)      return <div style={rv.stateCard}><p style={rv.errorText}>{error}</p></div>;
  if (!proposals) return <div style={rv.stateCard}><p style={rv.muted}>Loading proposals…</p></div>;

  const totalEntities = selItems.size + selGoals.size + selPeople.size + selRoutines.size;
  const totalSections = selSections.size;
  const sectionEntries = Object.entries(proposals.section_texts || {});

  // Verb breakdown across SELECTED entity proposals — drives the summary line so
  // the coordinator knows they're approving N new / M updates / K conflicts.
  const verbCounts = { new: 0, update: 0, conflict: 0 };
  const _tally = (list, sel) => (list || []).forEach((e, i) => {
    if (!sel.has(i)) return;
    const a = e._merge?.action;
    if (a === "update") verbCounts.update++;
    else if (a === "contradict") verbCounts.conflict++;
    else verbCounts.new++;
  });
  _tally(proposals.items, selItems);
  _tally(proposals.goals, selGoals);
  _tally(proposals.people, selPeople);
  _tally(proposals.routines, selRoutines);
  const hasVerbs = verbCounts.update > 0 || verbCounts.conflict > 0;

  const multiSource = [
    ...(proposals.items || []),
    ...(proposals.goals || []),
    ...(proposals.people || []),
    ...(proposals.routines || []),
  ].some(e => e.source && e.source !== "isp");

  return (
    <div style={rv.wrap}>
      <p style={rv.intro}>
        Uncheck anything that looks wrong before approving.
        {multiSource && " Source badges show which document contributed each item."}
      </p>

      <ContradictionsPanel contradictions={proposals.contradictions} />

      <EntitySection heading="Items" items={proposals.items}
        getLabel={i => i.title} selected={selItems} onToggle={toggle(setItems)} />
      <EntitySection heading="Goals" items={proposals.goals}
        getLabel={g => g.translated_text} selected={selGoals} onToggle={toggle(setGoals)} />
      <EntitySection heading="People" items={proposals.people}
        getLabel={p => p.display_name} selected={selPeople} onToggle={toggle(setPeople)} />
      <EntitySection heading="Routines" items={proposals.routines}
        getLabel={r => `${r.time_of_day} — ${r.title}`} selected={selRoutines} onToggle={toggle(setRoutines)} />

      {sectionEntries.length > 0 && (
        <div style={rv.section}>
          <p style={rv.sectionHead}>
            Section prose <span style={rv.count}>({sectionEntries.length})</span>
            <span style={rv.sectionHint}> — new material woven into the existing text</span>
          </p>
          {sectionEntries.map(([key, text]) => (
            <SectionTextCard key={key} sectionKey={key} text={text}
              checked={selSections.has(key)}
              onChange={v => setSections(prev => { const n = new Set(prev); if (v) n.add(key); else n.delete(key); return n; })} />
          ))}
        </div>
      )}

      {hasVerbs && (totalEntities > 0) && (
        <p style={rv.verbSummary}>
          Approving: {verbCounts.new > 0 && <span>{verbCounts.new} new</span>}
          {verbCounts.new > 0 && (verbCounts.update > 0 || verbCounts.conflict > 0) && ", "}
          {verbCounts.update > 0 && <span style={{ color: "#059669" }}>{verbCounts.update} update{verbCounts.update !== 1 ? "s" : ""}</span>}
          {verbCounts.update > 0 && verbCounts.conflict > 0 && ", "}
          {verbCounts.conflict > 0 && <span style={{ color: "#d97706" }}>{verbCounts.conflict} conflict{verbCounts.conflict !== 1 ? "s" : ""} to resolve</span>}
          {totalSections > 0 && `, ${totalSections} section${totalSections !== 1 ? "s" : ""}`}.
        </p>
      )}

      <button
        style={{ ...rv.approveBtn, ...(totalEntities === 0 && totalSections === 0 ? rv.approveBtnDisabled : {}) }}
        onClick={approve}
        disabled={approving || (totalEntities === 0 && totalSections === 0)}
      >
        {approving
          ? "Saving…"
          : `Approve ${totalEntities} record${totalEntities !== 1 ? "s" : ""} + ${totalSections} section${totalSections !== 1 ? "s" : ""}`}
      </button>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function ProposalCard({ item, label, checked, onChange }) {
  const merge = item._merge;
  const action = merge?.action;
  const isConflict = action === "contradict";
  return (
    <label style={{
      ...rv.card,
      ...(checked ? rv.cardChecked : {}),
      ...(isConflict ? rv.cardConflict : {}),
    }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
        style={{ flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", flexWrap: "wrap", marginBottom: 2 }}>
          <p style={rv.cardTitle}>{label}</p>
          <MergeChip merge={merge} />
          {item.source && item.source !== "isp" && <SourceBadge source={item.source} />}
        </div>
        {/* Update: show what changes, existing → incoming, when the merge carries it */}
        {action === "update" && (merge.existing_value || merge.incoming_value) && (
          <div style={rv.diffRow}>
            <span style={rv.diffOld}>{merge.existing_value || "—"}</span>
            <span style={rv.diffArrow}>→</span>
            <span style={rv.diffNew}>{merge.incoming_value || "—"}</span>
          </div>
        )}
        {isConflict && (
          <p style={rv.conflictNote}>
            {merge.description
              ? merge.description
              : "Disagrees with existing content."} Approving flags this for
            resolution — it won't overwrite the existing record.
          </p>
        )}
        {item.description && <p style={rv.cardBody}>{item.description}</p>}
        {item.purpose && <p style={rv.cardBody}>{item.purpose}</p>}
        {item.relationship && !item.description && <p style={rv.cardBody}>{item.relationship}</p>}
        {/* Consequence line — states BOTH checkbox states so unchecking never reads
            as deletion. Keyed by verb. Nothing here ever removes an existing record. */}
        <p style={rv.consequence}>
          {(() => {
            if (isConflict) {
              return checked
                ? "Will flag this conflict for you to resolve."
                : "Will ignore this conflict — existing record stays as it is.";
            }
            if (action === "update") {
              return checked
                ? `Will update ${label}.`
                : `Will keep ${label} unchanged.`;
            }
            // new / append / unverified
            return checked ? "Will be added." : "Won't be added.";
          })()}
        </p>
      </div>
    </label>
  );
}

// Verb chip driven by the _merge classification 1b attaches to each proposal.
// No block → "New" (seed proposals and unverified-new both create a record).
function MergeChip({ merge }) {
  const action = merge?.action;
  if (action === "update")     return <span style={{ ...mc.base, ...mc.update }}>Updates existing</span>;
  if (action === "contradict") return <span style={{ ...mc.base, ...mc.conflict }}>⚠ Conflicts</span>;
  // append or no classification
  return <span style={{ ...mc.base, ...mc.neu }}>New</span>;
}

const mc = {
  base:     { fontSize: 10, fontWeight: "var(--weight-semibold)", padding: "2px 7px", borderRadius: "var(--radius-full)", whiteSpace: "nowrap", flexShrink: 0, textTransform: "uppercase", letterSpacing: "0.04em" },
  neu:      { background: "var(--color-bg-inset)", color: "var(--color-text-tertiary)" },
  update:   { background: "rgba(16,185,129,0.10)", color: "#059669" },
  conflict: { background: "rgba(245,158,11,0.12)", color: "#d97706" },
};

function EntitySection({ heading, items, getLabel, selected, onToggle }) {
  if (!items?.length) return null;
  return (
    <div style={rv.section}>
      <p style={rv.sectionHead}>{heading} <span style={rv.count}>({items.length})</span></p>
      {items.map((item, i) => (
        <ProposalCard key={i} item={item} label={getLabel(item)}
          checked={selected.has(i)} onChange={v => onToggle(i, v)} />
      ))}
    </div>
  );
}

const SECTION_LABELS = {
  meet: "Meet", communicate: "Communication", sensory: "Sensory",
  passions: "Passions", day: "Daily Rhythm", working_on: "Working On",
  hard_times: "Hard Times", health: "Health", people: "People", community: "Community",
};

function SectionTextCard({ sectionKey, text, checked, onChange }) {
  // Real sources from extraction (Phase A). No ["isp"] fallback — an incremental
  // doc's sources are the real contributing doc type; an empty list shows no badge
  // rather than a misleading "ISP".
  const sources = text.sources || [];
  const base = text.base_snapshot;          // existing prose extraction composed against
  const proposed = text.lead_paragraph;     // enriched proposed prose
  const hasBase = typeof base === "string" && base.trim().length > 0;
  const isNewSection = !hasBase;             // no prior prose → brand-new section text

  return (
    <label style={{ ...rv.card, ...(checked ? rv.cardChecked : {}), flexDirection: "column", alignItems: "flex-start", gap: "var(--space-2)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", width: "100%" }}>
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
        <span style={rv.cardTitle}>{SECTION_LABELS[sectionKey] || sectionKey}</span>
        <span style={{ ...mc.base, ...(isNewSection ? mc.neu : mc.update) }}>
          {isNewSection ? "New text" : "Updates text"}
        </span>
        <div style={{ display: "flex", gap: "var(--space-1)", marginLeft: "auto", flexWrap: "wrap" }}>
          {sources.map(src => <SourceBadge key={src} source={src} />)}
        </div>
      </div>

      {/* Before → after. For an existing section, show the current prose struck
          through above the enriched version so the coordinator sees exactly what
          the new material changes. For a brand-new section, just the proposed text. */}
      {!isNewSection && proposed && (
        <div style={rv.proseDiff}>
          <span style={rv.proseDiffLabel}>Current</span>
          <p style={rv.proseOld}>{base}</p>
          <span style={rv.proseDiffLabel}>After weaving in {sources[0] ? "the new document" : "new material"}</span>
          <p style={rv.proseNew}>{proposed}</p>
        </div>
      )}
      {isNewSection && proposed && (
        <p style={{ ...rv.cardBody, margin: 0 }}>{proposed}</p>
      )}
      {text.tile_teaser && (
        <p style={{ fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", margin: 0 }}>
          Tile: {text.tile_teaser}
        </p>
      )}

      {/* Consequence line — states BOTH checkbox states so unchecking never reads
          as deletion. Nothing here removes the section; it only affects whether the
          new text is woven in. */}
      <p style={rv.consequence}>
        {checked
          ? (isNewSection ? "Will add this section text." : "Will update this section's text.")
          : (isNewSection ? "Won't add — section stays as it is." : "Will keep the current text unchanged.")}
      </p>
    </label>
  );
}

function ContradictionsPanel({ contradictions }) {
  if (!contradictions?.length) return null;
  return (
    <div style={ct.panel}>
      <div style={ct.header}>
        <span style={ct.icon}>⚠</span>
        <span style={ct.title}>{contradictions.length} contradiction{contradictions.length !== 1 ? "s" : ""} found</span>
      </div>
      <p style={ct.sub}>The ISP is authoritative. Review each conflict and decide which version to keep — you can edit records in the section editors after approving.</p>
      {contradictions.map((c, i) => (
        <div key={i} style={ct.row}>
          <p style={ct.desc}>{c.description}</p>
          <div style={ct.cols}>
            <div style={ct.col}>
              <p style={ct.colLabel}>ISP (authoritative)</p>
              <p style={ct.colVal}>{c.isp_value}</p>
            </div>
            <div style={ct.colDivider}>vs.</div>
            <div style={ct.col}>
              <p style={ct.colLabel}>{DOC_LABEL[c.other_source] || c.other_source}</p>
              <p style={ct.colVal}>{c.other_value}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Source badge (self-contained copy) ───────────────────────────────────────

const DOC_LABEL = {
  isp: "ISP",
  family_letter: "Family letter",
  shift_notes: "Shift notes",
  behavioral_plan: "Behavioral plan",
  health_plan: "Health plan",
  parent_interview: "Parent interview",
  other: "Other",
};

const SOURCE_COLORS = {
  isp:              { bg: "var(--color-accent-subtle)", text: "var(--color-accent)" },
  family_letter:    { bg: "rgba(139,92,246,0.08)",      text: "#7c3aed" },
  shift_notes:      { bg: "rgba(16,185,129,0.08)",      text: "#059669" },
  behavioral_plan:  { bg: "rgba(245,158,11,0.08)",      text: "#d97706" },
  health_plan:      { bg: "rgba(236,72,153,0.08)",      text: "#db2777" },
  parent_interview: { bg: "rgba(6,182,212,0.08)",       text: "#0891b2" },
  other:            { bg: "var(--color-bg-inset)",      text: "var(--color-text-tertiary)" },
};

function SourceBadge({ source }) {
  const colors = SOURCE_COLORS[source] || SOURCE_COLORS.other;
  return (
    <span style={{ fontSize: 10, fontWeight: "var(--weight-semibold)", padding: "2px 6px",
      borderRadius: "var(--radius-full)", background: colors.bg, color: colors.text,
      whiteSpace: "nowrap", flexShrink: 0, textTransform: "capitalize" }}>
      {DOC_LABEL[source] || source}
    </span>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────

const rv = {
  wrap:        { display: "flex", flexDirection: "column", gap: "var(--space-5)" },
  intro:       { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", margin: 0 },
  stateCard:   { padding: "var(--space-6)", textAlign: "center" },
  muted:       { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" },
  errorText:   { fontSize: "var(--text-sm)", color: "var(--color-critical)" },
  section:     { display: "flex", flexDirection: "column", gap: "var(--space-2)" },
  sectionHead: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.06em" },
  sectionHint: { fontWeight: "normal", textTransform: "none", letterSpacing: 0, marginLeft: "var(--space-2)", color: "var(--color-text-tertiary)" },
  count:       { fontWeight: "normal", color: "var(--color-text-tertiary)" },
  card:        { display: "flex", gap: "var(--space-3)", padding: "var(--space-3) var(--space-4)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", cursor: "pointer", background: "var(--color-bg-base)" },
  cardChecked: { borderColor: "var(--color-accent)", background: "var(--color-accent-subtle)" },
  cardConflict:{ borderColor: "rgba(245,158,11,0.45)" },
  cardTitle:   { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", color: "var(--color-text-primary)", margin: 0 },
  cardBody:    { fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", margin: 0, marginTop: 2 },
  diffRow:     { display: "flex", alignItems: "center", gap: "var(--space-2)", marginTop: 4, flexWrap: "wrap" },
  diffOld:     { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", textDecoration: "line-through", background: "var(--color-bg-inset)", borderRadius: "var(--radius-sm)", padding: "1px 6px" },
  diffArrow:   { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", flexShrink: 0 },
  diffNew:     { fontSize: "var(--text-xs)", color: "#059669", background: "rgba(16,185,129,0.08)", borderRadius: "var(--radius-sm)", padding: "1px 6px" },
  conflictNote:{ fontSize: "var(--text-xs)", color: "#92400e", margin: "4px 0 0", lineHeight: "var(--leading-normal)" },
  verbSummary: { fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", margin: 0 },
  consequence: { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", margin: "4px 0 0", fontStyle: "italic" },
  proseDiff:      { display: "flex", flexDirection: "column", gap: 3, width: "100%" },
  proseDiffLabel: { fontSize: 10, fontWeight: "var(--weight-semibold)", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-tertiary)" },
  proseOld:       { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", margin: 0, padding: "var(--space-2)", background: "var(--color-bg-inset)", borderRadius: "var(--radius-sm)", lineHeight: "var(--leading-normal)" },
  proseNew:       { fontSize: "var(--text-sm)", color: "var(--color-text-primary)", margin: "0 0 2px", padding: "var(--space-2)", background: "rgba(16,185,129,0.06)", borderRadius: "var(--radius-sm)", lineHeight: "var(--leading-normal)" },
  approveBtn:  { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", color: "#fff", background: "var(--color-accent)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-3) var(--space-5)", cursor: "pointer", alignSelf: "flex-start" },
  approveBtnDisabled: { opacity: 0.4, cursor: "not-allowed" },
};

const ct = {
  panel:  { background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.25)", borderRadius: "var(--radius-lg)", padding: "var(--space-4)", display: "flex", flexDirection: "column", gap: "var(--space-3)" },
  header: { display: "flex", alignItems: "center", gap: "var(--space-2)" },
  icon:   { fontSize: "var(--text-lg)", color: "#d97706" },
  title:  { fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", color: "#92400e" },
  sub:    { fontSize: "var(--text-xs)", color: "#92400e", lineHeight: "var(--leading-normal)", margin: 0 },
  row:    { padding: "var(--space-3)", background: "rgba(255,255,255,0.6)", borderRadius: "var(--radius-md)", display: "flex", flexDirection: "column", gap: "var(--space-2)" },
  desc:   { fontSize: "var(--text-sm)", color: "var(--color-text-primary)", margin: 0 },
  cols:   { display: "flex", gap: "var(--space-3)", alignItems: "flex-start" },
  col:    { flex: 1, display: "flex", flexDirection: "column", gap: 2 },
  colDivider: { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", alignSelf: "center", flexShrink: 0 },
  colLabel:   { fontSize: 10, fontWeight: "var(--weight-semibold)", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-tertiary)", margin: 0 },
  colVal:     { fontSize: "var(--text-sm)", color: "var(--color-text-primary)", background: "var(--color-bg-base)", borderRadius: "var(--radius-sm)", padding: "var(--space-1) var(--space-2)", margin: 0 },
};
